import {
  useCreateOrgPartMutation,
  useUploadOrgPartHeroImageSingleMutation,
} from 'store/slices/clientV2/parts';
import { getTrackingStrings } from 'constants/trackingStrings';
import {
  BASE_DEFAULT_VALUES,
  commonFields,
  formKeysForPartType,
  OrgPartFormConstants,
  OrgPartFormInputData,
  orgPartValidationSchema,
  OrgPartValidationSchemaFormData,
  OTS_WITH_PUBLIC_PART_FIELDS,
  PartTypesDefinitions,
} from 'ui-component/CreateNewOrgPartDialog/constants';
import { useMemo, useState, useCallback } from 'react';
import PartTypeSelector from 'ui-component/CreateNewOrgPartDialog/components/PartTypeSelector';
import { FormProvider, useWatch } from 'react-hook-form';
import _ from 'lodash';
import CreatePartForm from 'ui-component/CreateNewOrgPartDialog/components/CreatePartForm';
import { useTypedCreateRecordDialog } from 'hooks/typedRecordDialogs/useTypedCreateRecordDialog';
import {
  OrgPartPOSTSchema,
  OrgPartSchema,
  OrgPartType,
} from 'types/clientV2/parts';
import { Box, Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { ClientV2POSTResponse } from 'types/api';
import { handleErr } from 'utils/functions';
import useSnackbar from 'hooks/useSnackbar';
import { useGetUserQuery } from 'store/slices/apiV1/user';
import { ALL_RECORD_TYPE_IDS, ALL_RECORD_TYPES } from 'constants/recordTypes';
import { PartRaw } from 'types/part';
import { ALL_APPS, ALL_FEATURE_IDS } from 'constants/appConstants';
import { useCreateStockDocumentRelatedObjectMutation } from 'store/slices/clientV2/stockDocuments';
import { useAppAccessContext } from 'contexts/AppAccessContext';
import { appPermissionAccessLevels } from 'types/apps';
import {
  useApproveSupplierQuoteLineActionMutation,
  useCreateSupplierQuoteLineMutation,
  useCreateSupplierQuoteMutation,
} from 'store/slices/clientV2/supplierQuotes';
import {
  UNLIMITED_QUANTITY,
  UNLIMITED_QUANTITY_STRING,
} from 'constants/procurementConstants';
import { Price, SupplierQuoteLinePOSTSchema } from 'types/sourcing';
import { PackagingOptions } from 'types/inventory';
import { formatToIsoDateTime, getNowDate } from 'utils/dates';
import {
  DimensionClassOptions,
  UnitOfMeasureDetails,
  UnitOfMeasureOptions,
} from 'types/unitsOfMeasure';
import OtsPartWizard from 'ui-component/CreateNewOrgPartDialog/components/OtsPartWizard';
import { FeatureFlags } from 'types';
import { useFeature } from '@growthbook/growthbook-react';
import { OrgSupplier } from 'types/suppliers';

type CreateNewOrgPartDialogProps = {
  open: boolean;
  onClose: () => void;
  rawPart?: PartRaw;
  onCreateSuccess?: (newPartId: OrgPartSchema['id']) => void;
  searchPrefill?: string;
  skipSearch?: boolean;
  scrapePageContent?: string;
  orgSupplierId?: OrgSupplier['id'];
  onlyOtsMode?: boolean;
};

const CreateNewOrgPartDialog = ({
  open,
  onClose,
  rawPart,
  onCreateSuccess,
  searchPrefill,
  skipSearch,
  scrapePageContent,
  orgSupplierId,
  onlyOtsMode = false,
}: CreateNewOrgPartDialogProps) => {
  const tempOtsPartWizard = useFeature(FeatureFlags.tempOtsPartWizard).on;
  const { isPermissionDisabled, hasFeaturePermission } = useAppAccessContext();
  const simplePartCreationFeatureEnabled = hasFeaturePermission(
    ALL_FEATURE_IDS.SIMPLE_PART_CREATION
  );
  const [partTypeToCreate, setPartTypeToCreate] = useState<OrgPartType>(
    OrgPartType.OTS
  );
  const [heroImage, setHeroImage] = useState<File | null>(null);
  const [nonStockedPartMode, setNonStockedPartMode] = useState<
    OrgPartType.OTS | OrgPartType.CUSTOM
  >(OrgPartType.OTS);

  const { dispatchErrorSnackbar } = useSnackbar();
  const { data: user } = useGetUserQuery(undefined);

  const canCreateSupplierQuote = !isPermissionDisabled({
    app: ALL_APPS.SOURCING.id,
    accessLevel: appPermissionAccessLevels.edit,
  });

  const formRenderMode = useMemo(() => {
    if (partTypeToCreate === OrgPartType.NON_STOCKED) {
      return nonStockedPartMode;
    }
    return partTypeToCreate;
  }, [partTypeToCreate, nonStockedPartMode]);

  const {
    closeCreateNewOrgPartDialog,
    cancelCreateNewOrgPartDialog,
    saveCreateNewOrgPartDialog,
  } = getTrackingStrings();

  const [uploadHeroImage, { isLoading: isLoadingUploadHeroImage }] =
    useUploadOrgPartHeroImageSingleMutation();

  const [
    createStockDocumentRelatedObject,
    { isLoading: isLoadingCreateStockDocumentRelatedObject },
  ] = useCreateStockDocumentRelatedObjectMutation();

  const [createSupplierQuote, { isLoading: isLoadingCreateSupplierQuote }] =
    useCreateSupplierQuoteMutation();
  const [
    createSupplierQuoteLine,
    { isLoading: isLoadingCreateSupplierQuoteLine },
  ] = useCreateSupplierQuoteLineMutation();
  const [
    approveSupplierQuoteLine,
    { isLoading: isLoadingApproveSupplierQuoteLine },
  ] = useApproveSupplierQuoteLineActionMutation();

  const handlePreSubmit = (data: OrgPartValidationSchemaFormData) => {
    const pickKeys =
      formRenderMode === OrgPartType.OTS &&
      data[OrgPartFormConstants.publicPart.id]
        ? OTS_WITH_PUBLIC_PART_FIELDS
        : formKeysForPartType[formRenderMode];

    const payload = _.chain(data)
      .pick(pickKeys)
      .omitBy((val) => val === '')
      .value();
    payload[OrgPartFormConstants.partType.id] = partTypeToCreate;
    payload[OrgPartFormConstants.lastScrapedAt.id] = data[
      OrgPartFormConstants.link.id
    ]
      ? formatToIsoDateTime(getNowDate())
      : null;

    return payload as OrgPartPOSTSchema;
  };

  const defaultValues: OrgPartFormInputData = useMemo(
    () => ({
      ...BASE_DEFAULT_VALUES,
      [OrgPartFormConstants.partType.id]: partTypeToCreate,
      [OrgPartFormConstants.link.id]:
        rawPart?.link ?? BASE_DEFAULT_VALUES[OrgPartFormConstants.link.id],
      [OrgPartFormConstants.mfg.id]:
        rawPart?.mfg ||
        rawPart?.manufacturer ||
        BASE_DEFAULT_VALUES[OrgPartFormConstants.mfg.id],
      [OrgPartFormConstants.mpn.id]:
        rawPart?.mpn ?? BASE_DEFAULT_VALUES[OrgPartFormConstants.mpn.id],
      [OrgPartFormConstants.description.id]:
        rawPart?.description ??
        BASE_DEFAULT_VALUES[OrgPartFormConstants.description.id],
      [OrgPartFormConstants.isElectronicComponent.id]:
        partTypeToCreate !== OrgPartType.OTS
          ? false
          : user?.defaultToElectronicComponents ?? false,
      [OrgPartFormConstants.dimensionClass.id]: DimensionClassOptions.QUANTITY,
      [OrgPartFormConstants.preferredUnit.id]: UnitOfMeasureOptions.PIECE,
      [OrgPartFormConstants.orgSupplier.id]: orgSupplierId ?? undefined,
    }),
    [partTypeToCreate, user, rawPart, orgSupplierId]
  );

  const uploadHeroImageIfNeeded = async (newPartUUID: string) => {
    if (heroImage) {
      const formData = new FormData();
      formData.append('hero_image', heroImage);
      try {
        await uploadHeroImage({ payload: formData, id: newPartUUID }).unwrap();
      } catch (error) {
        handleErr(error, () => {
          dispatchErrorSnackbar('Failed to upload part image');
        });
      }
    }
  };

  const linkStockDocument = async (
    stockDocumentId: string,
    partId: string,
    supplierQuoteLineId?: string | null,
    supplierQuoteId?: string | null
  ) => {
    try {
      await createStockDocumentRelatedObject([
        {
          stockDocument: stockDocumentId,
          relatedObjectId: partId,
          relatedObjectType:
            ALL_RECORD_TYPES[ALL_RECORD_TYPE_IDS.V2_ORG_PART].flagshipModel,
        },
        ...(supplierQuoteLineId
          ? [
              {
                stockDocument: stockDocumentId,
                relatedObjectId: supplierQuoteLineId,
                relatedObjectType:
                  ALL_RECORD_TYPES[ALL_RECORD_TYPE_IDS.QUOTE_LINE]
                    .flagshipModel,
              },
            ]
          : []),
        ...(supplierQuoteId
          ? [
              {
                stockDocument: stockDocumentId,
                relatedObjectId: supplierQuoteId,
                relatedObjectType:
                  ALL_RECORD_TYPES[ALL_RECORD_TYPE_IDS.QUOTE].flagshipModel,
              },
            ]
          : []),
      ]).unwrap();
    } catch (error) {
      handleErr(error, () => {
        dispatchErrorSnackbar('Failed to link attachment');
      });
    }
  };

  const { methods, BaseCreateRecordDialog, dialogProps } =
    useTypedCreateRecordDialog<
      typeof orgPartValidationSchema,
      OrgPartPOSTSchema,
      OrgPartSchema
    >({
      validationSchema: orgPartValidationSchema,
      defaultValues,
      onSave: (createdIds) => handleOnSave(createdIds),
      useCreateMutation: useCreateOrgPartMutation,
      recordName: 'Part',
      trackingNames: {
        close: closeCreateNewOrgPartDialog,
        cancel: cancelCreateNewOrgPartDialog,
        save: saveCreateNewOrgPartDialog,
      },
      preSubmit: handlePreSubmit,
      recordType: ALL_RECORD_TYPE_IDS.V2_ORG_PART,
      allowAttachments: !(
        (simplePartCreationFeatureEnabled || tempOtsPartWizard) &&
        formRenderMode === OrgPartType.OTS
      ),
      navigateToPath: ALL_APPS.PARTS.path as string,
    });

  const {
    getValues,
    reset,
    formState: { isDirty },
  } = methods;

  const watchStockDocumentId = useWatch({
    control: methods.control,
    name: OrgPartFormConstants.stockDocument.id,
  });

  const watchFormData = useWatch({
    control: methods.control,
  });

  const createSupplierQuoteAndLine = async (newPartUUID: string) => {
    try {
      const supplierQuote = await createSupplierQuote({
        orgSupplier: watchFormData[OrgPartFormConstants.orgSupplier.id] || '',
      }).unwrap();

      const prices = watchFormData[OrgPartFormConstants.prices.id] ?? [];
      const maximum = watchFormData[OrgPartFormConstants.maximum.id];
      const maximumValue =
        maximum === UNLIMITED_QUANTITY_STRING
          ? UNLIMITED_QUANTITY
          : _.toNumber(maximum);

      const pricesPayload: Price[] = prices.map(
        (priceBreak: any, index: number) => ({
          from: priceBreak.to ?? 0,
          to:
            index === prices.length - 1
              ? maximumValue
              : prices[index + 1]?.to ?? maximumValue,
          unitPrice: priceBreak.unitPrice ?? 0,
          currency: 'USD',
        })
      );

      const payload: SupplierQuoteLinePOSTSchema = {
        supplierQuote: supplierQuote.createdIds[0],
        v2OrgPart: newPartUUID,
        moq: watchFormData[OrgPartFormConstants.moq.id] ?? null,
        multiple: watchFormData[OrgPartFormConstants.multiple.id] ?? null,
        maximum: maximumValue,
        prices: pricesPayload,
        shipsInLead: watchFormData[OrgPartFormConstants.shipsInLead.id] ?? null,
        coa: watchFormData[OrgPartFormConstants.coa.id] ?? null,
        packaging: watchFormData[
          OrgPartFormConstants.packaging.id
        ] as PackagingOptions,
        sku: watchFormData[OrgPartFormConstants.sku.id] ?? null,
        link: watchFormData[OrgPartFormConstants.link.id] ?? undefined,
        isAuthorized: true,
        lastScrapedAt: watchFormData[OrgPartFormConstants.link.id]
          ? formatToIsoDateTime(getNowDate())
          : null,
        dimensionValue:
          watchFormData[OrgPartFormConstants.dimensionValue.id] ?? 1,
        dimensionUnit:
          (watchFormData[
            OrgPartFormConstants.dimensionUnit.id
          ] as UnitOfMeasureOptions) ?? UnitOfMeasureOptions.PIECE,
        dimensionClass:
          UnitOfMeasureDetails[
            watchFormData[
              OrgPartFormConstants.dimensionUnit.id
            ] as UnitOfMeasureOptions
          ]?.dimensionClass ?? DimensionClassOptions.QUANTITY,
      };

      const supplierQuoteLine = await createSupplierQuoteLine(payload).unwrap();

      await approveSupplierQuoteLine({
        ids: [supplierQuoteLine.createdIds[0]],
      }).unwrap();

      return {
        supplierQuoteLineId: supplierQuoteLine.createdIds[0],
        supplierQuoteId: supplierQuote.createdIds[0],
      };
    } catch (error) {
      handleErr(error, () => {
        dispatchErrorSnackbar('Failed to create supplier quote');
      });
    }
    return null;
  };

  const handleOnSave = async (
    createdIds: ClientV2POSTResponse<OrgPartSchema>['createdIds']
  ) => {
    const newPartUUID = createdIds[0];
    // upload hero image file if one was provided
    await uploadHeroImageIfNeeded(newPartUUID);
    // create supplier quote and supplier quote line record if applicable
    let supplierQuoteLineId: string | undefined | null = null;
    let supplierQuoteId: string | undefined | null = null;

    if (
      canCreateSupplierQuote &&
      watchFormData[OrgPartFormConstants.orgSupplier.id] &&
      ((watchFormData[OrgPartFormConstants.maximum.id] || 0) > 0 ||
        watchFormData[OrgPartFormConstants.maximum.id] ===
          UNLIMITED_QUANTITY_STRING) &&
      (watchFormData[OrgPartFormConstants.prices.id] || []).length > 0
    ) {
      const createSupplierQuoteAndLineResponse =
        await createSupplierQuoteAndLine(newPartUUID);
      if (createSupplierQuoteAndLineResponse) {
        supplierQuoteLineId =
          createSupplierQuoteAndLineResponse.supplierQuoteLineId;
        supplierQuoteId = createSupplierQuoteAndLineResponse.supplierQuoteId;
      }
    }
    // link stock document if this was scraped from a URL
    if (watchStockDocumentId) {
      await linkStockDocument(
        watchStockDocumentId,
        newPartUUID,
        supplierQuoteLineId,
        supplierQuoteId
      );
    }
    if (onCreateSuccess) {
      onCreateSuccess(newPartUUID);
    }
  };

  const handlePartTypeChange = useCallback(
    (newPartType: OrgPartType) => {
      const currentValues = getValues();
      const fieldsToPreserve = [
        ...commonFields,
        OrgPartFormConstants.isElectronicComponent.id,
      ];
      const preservedValues = _.pick(currentValues, fieldsToPreserve);

      setPartTypeToCreate(newPartType);
      reset({
        ...defaultValues,
        ...preservedValues,
        [OrgPartFormConstants.partType.id]: newPartType,
        [OrgPartFormConstants.isElectronicComponent.id]:
          newPartType !== OrgPartType.OTS
            ? false
            : user?.defaultToElectronicComponents ?? false,
      });
    },
    [
      getValues,
      reset,
      defaultValues,
      setPartTypeToCreate,
      user?.defaultToElectronicComponents,
    ]
  );

  const handleClose = () => {
    reset(defaultValues);
    setPartTypeToCreate(OrgPartType.OTS);
    setHeroImage(null);
    onClose();
  };

  return (
    <BaseCreateRecordDialog
      open={open}
      onClose={handleClose}
      dialogProps={dialogProps}
      maxWidth={onlyOtsMode ? 'sm' : 'lg'}
      confirmClose={isDirty}
      isSubmitting={
        isLoadingCreateStockDocumentRelatedObject ||
        isLoadingCreateSupplierQuote ||
        isLoadingCreateSupplierQuoteLine ||
        isLoadingUploadHeroImage ||
        isLoadingApproveSupplierQuoteLine
      }
    >
      {!onlyOtsMode && (
        <PartTypeSelector
          partTypeToCreate={partTypeToCreate}
          setPartTypeToCreate={handlePartTypeChange}
        />
      )}
      <FormProvider {...methods}>
        <Box sx={{ mt: 6 }}>
          {partTypeToCreate === OrgPartType.NON_STOCKED && (
            <Stack alignItems="center">
              <ToggleButtonGroup
                size="large"
                color="primary"
                value={nonStockedPartMode}
                exclusive
                onChange={(
                  _event,
                  newMode: OrgPartType.OTS | OrgPartType.CUSTOM
                ) => setNonStockedPartMode(newMode)}
                sx={{ mb: 2 }}
              >
                <ToggleButton value={OrgPartType.OTS}>
                  {PartTypesDefinitions[OrgPartType.OTS].label}
                </ToggleButton>
                <ToggleButton value={OrgPartType.CUSTOM}>
                  {PartTypesDefinitions[OrgPartType.CUSTOM].label}
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          )}
          {(simplePartCreationFeatureEnabled ||
            tempOtsPartWizard ||
            onlyOtsMode) &&
          formRenderMode === OrgPartType.OTS ? (
            <OtsPartWizard
              setHeroImage={setHeroImage}
              defaultValues={defaultValues}
              searchPrefill={searchPrefill}
              skipSearch={skipSearch}
              scrapePageContent={scrapePageContent}
              orgSupplierId={orgSupplierId}
            />
          ) : (
            <CreatePartForm
              formRenderMode={formRenderMode}
              setHeroImage={setHeroImage}
            />
          )}
        </Box>
      </FormProvider>
    </BaseCreateRecordDialog>
  );
};

export default CreateNewOrgPartDialog;
